import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { hashKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/apiContext';
import { meQueryOptions, ME_QUERY_KEY } from '@/api/endpoints/me';
import {
  AccessNotProvisionedError,
  SecondFactorEnrollmentRequiredError,
  TotpReenrollmentRequiredError,
} from '@/api/errors';
import type { Session } from '@/types/session';
import { useAuth } from './authContext';
import type { SignOutOptions } from './authProvider';

export type SessionStatus =
  | 'anonymous'
  | 'loading'
  | 'ready'
  | 'not-provisioned'
  /** Signed in with no second factor. SessionGuard routes to enrollment. */
  | 'mfa-enrollment-required'
  /** Signed in with the SMS recovery factor. SessionGuard routes to re-enrollment. */
  | 'mfa-reenrollment-required'
  | 'error';

function isMfaRefusal(error: unknown): boolean {
  return (
    error instanceof SecondFactorEnrollmentRequiredError ||
    error instanceof TotpReenrollmentRequiredError
  );
}

export interface SessionState {
  status: SessionStatus;
  session: Session | null;
  error: Error | null;
  refetch: () => void;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const api = useApi();

  const query = useQuery({
    ...meQueryOptions(api),
    // Being authenticated with the IdP does not mean being authorised for the
    // dashboard, and the reverse is also true: there is nothing to ask about
    // until the auth provider has an account.
    enabled: isAuthenticated,
  });

  // /me is cached for a long time, so a session can be 'ready' when the
  // backend starts refusing its token for MFA (enforcement switched on
  // mid-session). Any other request that meets that refusal re-asks /me, and
  // the status below then routes the user. /me itself is excluded: its own
  // failure is what the status reads, and re-asking would loop.
  const queryClient = useQueryClient();
  useEffect(() => {
    const meHash = hashKey(ME_QUERY_KEY);
    const recheck = (error: unknown, queryHash?: string) => {
      if (!isMfaRefusal(error) || queryHash === meHash) return;
      void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    };
    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        recheck(event.action.error, event.query.queryHash);
      }
    });
    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        recheck(event.action.error);
      }
    });
    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [queryClient]);

  // The MFA refusals are checked before `data`: a refetch that fails keeps the
  // previous data, and a session re-asked because of an MFA refusal elsewhere
  // (above) must stop being 'ready' even though its old answer is still there.
  const status: SessionStatus = !isAuthenticated
    ? 'anonymous'
    : query.error instanceof SecondFactorEnrollmentRequiredError
      ? 'mfa-enrollment-required'
      : query.error instanceof TotpReenrollmentRequiredError
        ? 'mfa-reenrollment-required'
        : query.data
          ? 'ready'
          : query.error instanceof AccessNotProvisionedError
            ? 'not-provisioned'
            : query.error
              ? 'error'
              : 'loading';

  const value: SessionState = {
    status,
    session: status === 'ready' ? (query.data ?? null) : null,
    error: query.error ?? null,
    refetch: () => void query.refetch(),
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** The raw state, including the not-yet-resolved cases. Guards use this. */
export function useSessionState(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSessionState must be used inside <SessionProvider>.');
  return value;
}

/**
 * The resolved session. Safe to call anywhere inside <SessionGuard>, which is
 * everywhere a feature screen renders.
 */
export function useSession(): Session {
  const { session } = useSessionState();
  if (!session) {
    throw new Error('useSession was called outside <SessionGuard>; no session is resolved.');
  }
  return session;
}

/**
 * Sign out, clearing both caches in the same handler.
 *
 * Clearing the auth provider's state without clearing the query cache leaves the previous
 * user's data rendered behind the sign-out transition — visible for a moment,
 * and a genuine cross-role leak on a shared device.
 */
export function useSignOut(): (options?: SignOutOptions) => Promise<void> {
  const { signOut } = useAuth();
  const queryClient = useQueryClient();

  return async (options) => {
    queryClient.removeQueries({ queryKey: ME_QUERY_KEY });
    queryClient.clear();
    await signOut(options);
  };
}

/**
 * Cosmetic only. Hiding a control the role cannot use is a courtesy to the
 * user, never a security control: the backend enforces the same rules
 * independently, and the app must remain correct if someone edits the
 * permissions array in memory.
 */
export function useHasPermission(permission: string): boolean {
  const { session } = useSessionState();
  return session?.permissions.includes(permission) ?? false;
}
